import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';

const ViewAllArticlesCard = ({ 
  onMouseEnter, 
  onMouseLeave,
  className = '',
  style = {}
}) => {
  const { t } = useTranslation();
  const { currentLanguage } = useLanguage();

  const handleMouseEnter = (e) => {
    // Card hover effects
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.4)';
    
    // Icon hover effects
    const svg = e.currentTarget.querySelector('svg');
    if (svg) {
      svg.style.transform = 'scale(1.1)';
    }
    
    // Overlay hover effects
    const overlay = e.currentTarget.querySelector('.card-hover-overlay');
    if (overlay) {
      overlay.style.opacity = '1';
    }
    
    if (onMouseEnter) onMouseEnter(e);
  };

  const handleMouseLeave = (e) => {
    // Reset card effects
    e.currentTarget.style.transform = 'translateY(0)';
    e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.1)';
    
    // Reset icon effects
    const svg = e.currentTarget.querySelector('svg');
    if (svg) {
      svg.style.transform = 'scale(1)';
    }
    
    // Reset overlay effects
    const overlay = e.currentTarget.querySelector('.card-hover-overlay');
    if (overlay) {
      overlay.style.opacity = '0';
    }
    
    if (onMouseLeave) onMouseLeave(e);
  };

  return (
    <article
      className={`masonry-cards_item promoted-item card card--background-image ${className}`}
      style={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "12px",
        transition: "transform 0.3s ease, box-shadow 0.3s ease",
        boxSizing: "border-box",
        minHeight: "300px",
        height: "100%",
        margin: "0px",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        background: "linear-gradient(135deg, #1a1a1a 0%, #000000 100%)",
        cursor: "pointer",
        ...style
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Pattern overlay for visual interest */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: `
          radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
          radial-gradient(circle at 40% 40%, rgba(255, 255, 255, 0.05) 0%, transparent 50%)
        `,
        zIndex: 1
      }} />

      {/* Content Container */}
      <Link
        to={`/${currentLanguage}/insights`}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          textDecoration: 'none',
          color: 'white',
          zIndex: 2,
          padding: '24px',
          textAlign: 'center',
          boxSizing: 'border-box'
        }}
      >
        {/* Icon */}
        <div style={{
          marginBottom: '20px',
          transform: 'scale(1)',
          transition: 'transform 0.3s ease'
        }}>
          <svg 
            width="48" 
            height="48" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="1.5"
            style={{
              filter: 'drop-shadow(0 4px 8px rgba(0, 0, 0, 0.3))'
            }}
          >
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/>
            <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
            <path d="M6 8h2"/>
            <path d="M6 12h2"/>
            <path d="M16 8h2"/>
            <path d="M16 12h2"/>
          </svg>
        </div>

        {/* Title */}
        <h3 style={{
          margin: "0px 0px 16px 0px",
          fontFamily: "Inter, sans-serif",
          fontSize: "clamp(18px, 2.5vw, 24px)",
          fontWeight: 700,
          lineHeight: "1.2em",
          color: "white",
          letterSpacing: "-0.02em",
          textShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
          textAlign: 'center'
        }}>
          {currentLanguage === 'ar' ? 'جميع المقالات' : 'All Articles'}
        </h3>

        {/* Description */}
        <p style={{
          margin: "0px 0px 24px 0px",
          fontFamily: "Inter, sans-serif",
          fontSize: "clamp(13px, 1.8vw, 16px)",
          fontWeight: 400,
          lineHeight: "1.5em",
          color: "rgba(255, 255, 255, 0.9)",
          textAlign: 'center',
          maxWidth: '200px'
        }}>
          {currentLanguage === 'ar' 
            ? 'استكشف جميع مقالاتنا القانونية والرؤى المهنية'
            : 'Explore all our legal articles and professional insights'
          }
        </p>

        {/* CTA Button */}
        <div style={{
          padding: "12px 24px",
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          borderRadius: "30px",
          fontSize: "11px",
          fontWeight: 700,
          color: "white",
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          border: '2px solid rgba(255, 255, 255, 0.2)',
          backdropFilter: 'blur(10px)',
          transition: 'all 0.3s ease',
          textAlign: 'center',
          minWidth: '140px'
        }}>
          {currentLanguage === 'ar' ? 'عرض الكل' : 'View All'}
        </div>
      </Link>

      {/* Hover effect overlay */}
      <div 
        className="card-hover-overlay"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
          opacity: 0,
          transition: 'opacity 0.3s ease',
          zIndex: 3,
          pointerEvents: 'none'
        }}
      />

    </article>
  );
};

export default ViewAllArticlesCard;
